/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef } from 'react';

import { ThumbsDown, ThumbsUp, Hourglass, Table2 } from 'lucide-react';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { Spin, Modal, Tooltip, Table } from 'antd';
import moment from 'moment';
import {
  OnFetchLogDetails,
  OnFetchPqtSowStationTests,
} from '../../redux/PQTlogDetails/saga';
import { PROJECT_TABLE_LOADER_INDICATOR, getProjectTableLoadingConfig } from '../ProjectTableLoader';
import {
  ACCEPT_CELL_COLOR,
  REJECT_CELL_COLOR,
  calculateInspectionTestStatus,
  formatTableCellWithUnit,
  getTableRowRangeStatus,
  isTableMultipleComparatorMode,
  parseJsonCellValue,
  parseTestTableData,
} from '../../utils/inspectionLogCell';
import {
  PqtMandatoryTestLabel,
  isPqtTestMandatory,
  resolvePqtTestMandatory,
} from '../../utils/pqtTestLabel';

/**
 * PQT Results matrix — loads inspection report via the existing
 * `pqt-report/inspection-report-data` API for the toolbar start/end dates.
 */
const LogCalender = (props) => {
  const {
    selectedSOW,
    /** `{ fromDate, toDate }` from toolbar date pickers (YYYY-MM-DD) */
    activeLogDateRange,
    onResultsCalendarNavigate,
    /** Increment from parent after recalculate to reload rows */
    inspectionLogRefreshKey = 0,
  } = props;

  const [loading, setLoading] = useState(false);
  const [isResultModalVisible, setIsResultModalVisible] = useState(false);
  const [selectDataSource, setSelectDataSource] = useState([]);
  const [dataResultTable, setDataResultTable] = useState([]);
  const [pqtStationTests, setPqtStationTests] = useState([]);

  const activeLogDateRangeRef = useRef(activeLogDateRange);
  activeLogDateRangeRef.current = activeLogDateRange;

  const notifyParentDate = (dayStr) => {
    if (typeof onResultsCalendarNavigate !== 'function' || !dayStr) return;
    const d = moment(dayStr, 'YYYY-MM-DD');
    if (!d.isValid()) return;
    onResultsCalendarNavigate({
      day: dayStr,
      mode: 'year',
      year: d.year(),
      month: d.format('MM'),
    });
  };

  const loadPqtStationTests = async (sowId) => {
    if (!sowId) {
      setPqtStationTests([]);
      return;
    }
    const tests = await OnFetchPqtSowStationTests(sowId);
    setPqtStationTests(tests);
  };

  useEffect(() => {
    if (!selectedSOW?.id) {
      setPqtStationTests([]);
      setSelectDataSource([]);
      return;
    }
    loadPqtStationTests(selectedSOW.id);
  }, [selectedSOW?.id]);

  /** Load inspection matrix whenever SOW or toolbar date range changes. */
  useEffect(() => {
    const from = activeLogDateRange?.fromDate;
    const to = activeLogDateRange?.toDate;
    if (!selectedSOW?.id || !from || !to) {
      setSelectDataSource([]);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const logObj = {
          fromDate: from,
          toDate: to,
          sowId: selectedSOW.id,
          pageSize: 1000,
          pageIndex: 0,
        };
        const res = await OnFetchLogDetails(logObj);
        if (cancelled) return;
        if (res?.dataSource?.[0]?.length > 0) {
          setSelectDataSource(res.dataSource[0]);
        } else {
          setSelectDataSource([]);
        }
        notifyParentDate(to);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSOW?.id, activeLogDateRange?.fromDate, activeLogDateRange?.toDate]);

  /** Refetch after Recalculate using the same date range. */
  useEffect(() => {
    if (!inspectionLogRefreshKey) return;
    if (!selectedSOW?.id) return;
    const from = activeLogDateRangeRef.current?.fromDate;
    const to = activeLogDateRangeRef.current?.toDate;
    if (!from || !to) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const logObj = {
          fromDate: from,
          toDate: to,
          sowId: selectedSOW.id,
          pageSize: 1000,
          pageIndex: 0,
        };
        const res1 = await OnFetchLogDetails(logObj);
        if (cancelled) return;
        if (res1?.dataSource?.[0]?.length > 0) {
          setSelectDataSource(res1.dataSource[0]);
        } else {
          setSelectDataSource([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inspectionLogRefreshKey, selectedSOW?.id]);

  const transformData = () => {
    let transformedRows = {};

    const weldIds = [...new Set(selectDataSource.map((item) => item.weld_id))];

    const getUniString = (item) => {
      if (item.value == null || String(item.value).trim() === '') return '-';

      const raw = String(item.value).trim();
      const unit = item.test_value_type?.display_str || '';

      const parseAsJsonArray = (str) => {
        const t = str.trim();
        if (!(t.startsWith('[') && t.endsWith(']'))) return null;
        try {
          const p = JSON.parse(t);
          return Array.isArray(p) ? p : null;
        } catch {
          try {
            const p = JSON.parse(t.replace(/\\"/g, '"'));
            return Array.isArray(p) ? p : null;
          } catch {
            return null;
          }
        }
      };

      const cleanSegment = (s) =>
        String(s)
          .trim()
          .replace(/\\"/g, '"')
          .replace(/^["']+|["']+$/g, '')
          .replace(/^\[|\]$/g, '')
          .trim();

      let parts;
      const parsed = parseAsJsonArray(raw);
      if (parsed) {
        parts = parsed.map((v) => (v == null ? '' : String(v).trim())).filter((p) => p !== '');
      } else {
        parts = raw
          .replace(/^\[|\]$/g, '')
          .split(',')
          .map((s) => cleanSegment(s))
          .filter((p) => p !== '');
      }

      if (parts.length === 0) return '-';

      const body = parts.join(' / ').replace(/\[|\]/g, '').trim();
      return unit ? `${body} ${unit}`.trim() : body;
    };

    selectDataSource
      .filter((item) => item.process_description !== 'PROCESS DESCRIPTION')
      .forEach((item) => {
        const processKey = item.process_description;

        const resolvedMandatory = resolvePqtTestMandatory(
          processKey,
          item.mandatory ?? item.is_mandatory ?? item.isMandatory,
          pqtStationTests
        );

        if (!transformedRows[processKey]) {
          transformedRows[processKey] = {
            key: processKey,
            process_description: processKey,
            mandatory: resolvedMandatory,
          };

          weldIds.forEach((weldId) => {
            transformedRows[processKey][weldId] = '-';
          });
        } else if (
          !isPqtTestMandatory(transformedRows[processKey].mandatory) &&
          isPqtTestMandatory(resolvedMandatory)
        ) {
          transformedRows[processKey].mandatory = resolvedMandatory;
        }

        const rejectStyle = { color: REJECT_CELL_COLOR };
        if (item.type_of_test === 'Table') {
          const status = calculateInspectionTestStatus(item);
          const iconColor =
            status === 'reject'
              ? REJECT_CELL_COLOR
              : status === 'accept'
                ? ACCEPT_CELL_COLOR
                : undefined;
          transformedRows[processKey][item.weld_id] =
            item.value && item.value.trim() !== '' ? (
              <span style={iconColor ? { color: iconColor } : undefined}>
                <Table2
                  role="button"
                  tabIndex={0}
                  className="pqt-results-table-open-icon"
                  size={20}
                  strokeWidth={2}
                  aria-label="Open table"
                  onClick={() => {
                    setDataResultTable(item);
                    setIsResultModalVisible(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setDataResultTable(item);
                      setIsResultModalVisible(true);
                    }
                  }}
                />
              </span>
            ) : (
              '-'
            );
        } else if (item.type_of_test === 'Value') {
          const status = calculateInspectionTestStatus(item);
          const isReject = status === 'reject';
          transformedRows[processKey][item.weld_id] = (
            <span style={isReject ? rejectStyle : undefined}>{getUniString(item)}</span>
          );
        } else {
          transformedRows[processKey][item.weld_id] = item.value || '-';
        }
      });

    let finalRows = Object.values(transformedRows);

    ['repair_status', 'overall_status'].forEach((param) => {
      let row = { key: param, process_description: param.replace(/_/g, ' ').toUpperCase() };

      weldIds.forEach((weldId) => {
        const record = selectDataSource.find((item) => item.weld_id === weldId);
        row[weldId] = record ? record[param] : '-';
      });

      finalRows.push(row);
    });

    return finalRows;
  };

  const getColumnData = () => {
    const weldIds = [...new Set(selectDataSource.map((item) => item.weld_id))];

    const columns = [
      {
        title: 'Process Description',
        dataIndex: 'process_description',
        key: 'process_description',
        width: 200,
        fixed: true,
        render: (text, record) => (
          <Tooltip
            title={
              isPqtTestMandatory(record.mandatory)
                ? `${text} (mandatory test)`
                : text
            }
          >
            <span className="pqt-log-process-description">
              <PqtMandatoryTestLabel label={text} mandatory={record.mandatory} />
            </span>
          </Tooltip>
        ),
      },
      ...weldIds.map((weldId) => ({
        title: weldId,
        dataIndex: weldId,
        key: weldId,
        align: 'center',
        width: 120,
        render: (text) =>
          text === 'accept' ? (
            <Tooltip title="Accepted">
              <ThumbsUp
                className="pqt-results-status-icon pqt-results-status-icon--accept"
                size={20}
                strokeWidth={2}
                aria-label="Accepted"
              />
            </Tooltip>
          ) : text === 'reject' ? (
            <Tooltip title="Rejected">
              <ThumbsDown
                className="pqt-results-status-icon pqt-results-status-icon--reject"
                size={20}
                strokeWidth={2}
                aria-label="Rejected"
              />
            </Tooltip>
          ) : text === 'pending' ? (
            <Tooltip title="Pending">
              <Hourglass
                className="pqt-results-status-icon pqt-results-status-icon--pending"
                size={20}
                strokeWidth={2}
                aria-hidden
              />
            </Tooltip>
          ) : (
            text
          ),
      })),
    ];

    return columns;
  };

  const getTableColumns = (item) => {
    if (!item || !item.value) return [];

    const unit = item.test_value_type?.display_str || '';
    const parsedValueData = parseJsonCellValue(item.value);
    const showRowLevelStatus = isTableMultipleComparatorMode(item);
    let columns = [
      {
        title: `${unit}`,
        dataIndex: 'name',
        key: 'name',
        render: showRowLevelStatus
          ? (text, record) => {
              const rowIndex = record.key;
              const valueRow = parsedValueData?.rows?.[rowIndex];
              const templateRow = parseTestTableData(item)?.rows?.[rowIndex];
              const rowStatus = getTableRowRangeStatus(valueRow, templateRow, item);
              if (rowStatus === false) {
                return <span style={{ color: REJECT_CELL_COLOR }}>{text}</span>;
              }
              return text;
            }
          : undefined,
      },
    ];

    try {
      const parsedData = parseTestTableData(item);

      if (!parsedData?.columns || !Array.isArray(parsedData.columns)) return columns;

      parsedData.columns.forEach((col, index) => {
        columns.push({
          title: col,
          dataIndex: `col_${index}`,
          key: `col_${index}`,
          align: 'center',
        });
      });
    } catch (error) {
      console.error('Error parsing JSON in getTableColumns:', error);
    }

    return columns;
  };

  const getTableData = (item) => {
    if (!item || !item.value) return [];

    try {
      const parsedData = parseJsonCellValue(item.value);
      const templateRows = parseTestTableData(item);
      if (!parsedData?.rows || !Array.isArray(parsedData.rows)) return [];
      if (!templateRows?.rows || !Array.isArray(templateRows.rows)) return [];

      const fallbackUnit = item.test_value_type?.display_str || '';

      return templateRows.rows.map((row, rowIndex) => {
        const valueRow = parsedData.rows[rowIndex];
        const cellValues = Array.isArray(valueRow?.values) ? valueRow.values : [];
        const numCols = Math.max(
          cellValues.length,
          row.values?.length ?? 0,
          parsedData.columns?.length ?? 0,
          templateRows.columns?.length ?? 0
        );

        const rowData = { key: rowIndex, name: row.name };
        for (let index = 0; index < numCols; index++) {
          const value = cellValues[index] ?? '';
          rowData[`col_${index}`] = formatTableCellWithUnit(value, row.unit, fallbackUnit);
        }
        return rowData;
      });
    } catch (error) {
      console.error('Error parsing JSON in getTableData:', error);
      return [];
    }
  };

  return (
    <div className="pqt-results-log-full">
      <div className="pqt-results-log-full__table">
        {loading ? (
          <div className="pqt-results-log-full__loading">
            <Spin size="large" indicator={PROJECT_TABLE_LOADER_INDICATOR} />
          </div>
        ) : selectDataSource && selectDataSource.length > 0 ? (
          <div className="addPQTDetails">
            <Table
              columns={getColumnData()}
              dataSource={transformData()}
              pagination={false}
              width="100%"
              scroll={{ x: 'max-content', y: 'calc(100vh - 260px)' }}
              className="pqt-results-table-shell"
            />
          </div>
        ) : (
          <Table
            loading={getProjectTableLoadingConfig(loading)}
            className="pqt-results-table-shell"
          />
        )}
      </div>

      <Modal
        width="50vw"
        wrapClassName="pqt-results-table-modal-wrap"
        className="pqt-results-table-modal"
        title="Table Results"
        centered
        visible={isResultModalVisible}
        footer={[
          <CrceButton
            key="cancel"
            label="Close"
            variant="tertiary"
            size="medium"
            onClick={() => {
              setIsResultModalVisible(false);
            }}
          />,
        ]}
      >
        <div>
          <Table
            columns={getTableColumns(dataResultTable)}
            dataSource={getTableData(dataResultTable)}
            width={'100%'}
            pagination={false}
            scroll={{ x: 'max-content' }}
            className="pqt-results-modal-inner-table"
          />
        </div>
      </Modal>
    </div>
  );
};

export default LogCalender;
